import camelCase from 'lodash/camelCase';

export const toPrettyText = (input: string | object) => {
  if (!input) return '';
  if (typeof input === 'object') return JSON.stringify(input, null, 2);

  // if the embedded text is in JSON format, de-stringify and format for display
  try {
    return JSON.stringify(JSON.parse(input), null, 2); // this is how you parse a string into JSON
    // eslint-disable-next-line no-empty
  } catch {} // ignore if not json

  return input;
};

export const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Removes all non-alphanumeric characters from a string
export const normaliseString = (str: string): string =>
  str.toLowerCase().replace(/[^a-z0-9]/g, '');

type StringDelimeters = '-' | '_' | ' ' | ',' | '.'; // Add more if needed

// Conerts provided literal to a camelCase literal by removing delimeters and capitalising the succeeding character
type ToCamelCase<S extends string> = S extends `${infer First}${infer Rest}`
  ? First extends StringDelimeters
    ? ToCamelCase<Capitalize<Rest>>
    : `${First}${ToCamelCase<Rest>}`
  : S;

type CamelCase<S extends string> =
  S extends Uppercase<S>
    ? Lowercase<S>
    : Lowercase<S> extends ToCamelCase<Lowercase<S>>
      ? Uncapitalize<S>
      : ToCamelCase<Lowercase<S>>;

type NarrowCamelCase<T extends string> = T extends unknown
  ? CamelCase<T>
  : never;

// Converts string literal to camelcase *For non literal values, please use lodash camelCase function
export const literalToCamelCase = <S extends string>(
  value: S
): NarrowCamelCase<S> => camelCase(value) as NarrowCamelCase<S>;

type EnumToCamelCase<T extends string[]> = {
  [K in keyof T]: T[K] extends string ? CamelCase<T[K]> : T[K];
};

export const arrayLiteralToCamelCase = <E extends string[]>(
  enumArray: E
): EnumToCamelCase<E> =>
  enumArray.map((value) => literalToCamelCase(value)) as EnumToCamelCase<E>;

export const camelCaseToWords = (value: string) =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

/** Extracts initials from a full name (e.g. "John Smith" → "JS") */
export const getInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .map((part) => part.substring(0, 1).toUpperCase())
    .join('');
